import React, { FunctionComponent, useState } from 'react';
import produce from 'immer';

import { UpdatePageModel } from '../UpdatePageModel';
import { Alert, ButtonCountdown, ConfirmIcon, ErrorMessage } from 'components';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader} from '@cfa/react-components';
import { AsyncStatus, UserRole } from 'enums';
import { useLuxonTimezone } from 'hooks';
import { parseAxiosError, UsersService } from 'services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateAndTime } from 'services/Utils';

interface IConfirmModalProps {
    model: UpdatePageModel,
    show: boolean,
    stageId: number,
    onHide: () => void,
}

interface IStageModalState {
    confirmError?: string,
    confirmStatus: AsyncStatus,
}

export function ConfirmModal({ model, onHide, show, stageId }: IConfirmModalProps) {
    // Prevent errors while the page is loading. This doesn't break hooks because we recreate the modal on open.
    if (model.loadStatus !== AsyncStatus.Success || !stageId) return null;

    const [state, setState] = useState<IStageModalState>({
        confirmStatus: AsyncStatus.Initial,
    });
    const { confirmStatus, confirmError } = state;
    const { update } = model;
    const { timezone, hasTimezone } = useLuxonTimezone(UsersService.me.timezone);

    // Lookup the stage from the model to avoid having to pass in an up-to-date stage object.
    // The lookup logic is easier to do here rather than in the board.
    const stageIndex = model.update.rollout.stages.findIndex(stage => stage.id === stageId);
    const stage = (stageIndex !== -1) && model.update.rollout.stages[stageIndex];
    const stageNum = stageIndex + 1;

    const hasEditPerm = UsersService.oktaUserInfo?.role >= UserRole.LimitedUser;
    const canConfirm = model.canConfirmStage(update, stage, stageIndex);

    let notConfirmedMsg = `You can only confirm a stage after the previous stage has started running.`
    if (model.isChangingSchedule) {
        notConfirmedMsg = `You must complete the schedule change before confirming this stage.`;
    }

    function onConfirm() {
        if (model.apiCallStatus === AsyncStatus.Loading) return;

        setState(produce(draft => {
            draft.confirmError = '';
            draft.confirmStatus = AsyncStatus.Loading;
        }))

        model.confirmStage(stageNum - 1)
            .then(() => {
                setState(produce(draft => {
                    draft.confirmStatus = AsyncStatus.Success;
                }))
            })
            .catch((err) => {
                setState(produce(draft => {
                    draft.confirmStatus = AsyncStatus.Error;
                    draft.confirmError = parseAxiosError(err);
                }))
            })
    }

    return (
        <Modal show={show} onClose={onHide} size="lg" className="stageModal">
            <ModalHeader>
                <ConfirmIcon isConfirmed={stage.isConfirmed} />
                &nbsp;Confirm Stage {stageNum}
            </ModalHeader>
            <ModalBody>
                <p className="confirmModal_title">
                    {!stage.isConfirmed && (`Stage ${stageNum} needs confirmation before it will be allowed to start.`)}
                    {stage.isConfirmed && (`Stage ${stageNum} is confirmed.`)}
                </p>
                <ul>
                    <li>Starts at <b>{formatDateAndTime(stage.startTime, timezone, hasTimezone)}</b>.</li>
                    <li>Review results from previous stage before proceeding with confirmation.</li>
                </ul>
                {confirmError && <ErrorMessage message={confirmError} />}
                {stage.isConfirmed && (
                    <Alert type="success">
                        <FontAwesomeIcon icon="check" />&nbsp;
                        Stage {stageNum} is confirmed and will start automatically.
                    </Alert>
                )}
                {!stage.isConfirmed && !canConfirm && (
                    <Alert type="info">
                        <FontAwesomeIcon icon="info-circle" />&nbsp;
                        {notConfirmedMsg}
                    </Alert>
                )}
            </ModalBody>
            <ModalFooter>
                <Button 
                    variant={"outlined"}
                    onClick={onHide} 
                    disabled={confirmStatus === AsyncStatus.Loading}
                >
                    Close
                </Button>
                {!stage.isConfirmed && (
                    <ButtonCountdown
                        style={{marginLeft: "10px"}}
                        onClick={onConfirm}
                        loading={confirmStatus === AsyncStatus.Loading}
                        disabled={!hasEditPerm || !canConfirm || confirmStatus === AsyncStatus.Success }
                    >
                        {confirmStatus !== AsyncStatus.Loading && 'Confirm'}
                        {confirmStatus === AsyncStatus.Loading && 'Confirming...'}
                    </ButtonCountdown>
                )}
            </ModalFooter>
        </Modal>
    );
}